/**
 * The main application declaration.
 */

/* global Modernizr, $ */
import 'jquery';
import 'underscore';
import moment from 'moment';
import 'font-awesome/css/font-awesome.min.css';

import angular from 'angular';
import ngRoute from 'angular-route';
import ngTouch from 'angular-touch';
import ngSanitize from 'angular-sanitize';

import './js/lib/modernizr-custom-3.3.1.min';
import 'bootstrap-sass/assets/javascripts/bootstrap';
import 'mousetrap';
import 'mousetrap/plugins/bind-dictionary/mousetrap-bind-dictionary';
import 'mousetrap/plugins/global-bind/mousetrap-global-bind';
import 'requirejs/require';

import 'ace-ui';

import ContentDeveloperDashboardCtrl from './views/dashboard-view/dashboard-view-controller';
import ContentDeveloperMainController from './views/main-view/main-view-controller';
import ContentDeveloperErrorController from './views/error-view/error-view-controller';
import ContentDeveloperFullSearchController from './views/search-view/search-view-controller';
import ContentDeveloperEditVersionController from './components/edit-version-controller/edit-version-controller';

import MainViewTemplate from './views/main-view/main-view.html';
import ErrorTemplate from './views/error-view/error-view.html';
import SearchTemplate from './views/search-view/search-view.html';
import DashboardTemplate from './views/dashboard-view/dashboard-view.html';

import ComponentsModule from './components/components-module';
import './components/components';

import './main.scss';

import uibAccordion from 'angular-ui-bootstrap/src/accordion';
import uibButtons from 'angular-ui-bootstrap/src/buttons';
import uibCollapse from 'angular-ui-bootstrap/src/collapse';
import uibPopover from 'angular-ui-bootstrap/src/popover';

const app = angular.module('atex.code', [ngRoute, ngSanitize, ngTouch, ComponentsModule, uibAccordion, uibCollapse, uibPopover, uibButtons, 'atex.aceui.service', 'atex.aceui.authentication', 'atex.aceui.ui.widgets'])
  .controller('ContentDeveloperDashboardCtrl', ContentDeveloperDashboardCtrl)
  .controller('ContentDeveloperEditVersionController', ContentDeveloperEditVersionController)
  .controller('ContentDeveloperErrorController', ContentDeveloperErrorController)
  .controller('ContentDeveloperFullSearchController', ContentDeveloperFullSearchController)
  .controller('ContentDeveloperMainController', ContentDeveloperMainController)
  .controller('popoverControl', function ($scope) {
    $scope.closePopover = () => { $scope.popoverIsOpen = false; };
  });

app.config(function ($routeProvider, $compileProvider, $provide, $locationProvider, $translateProvider) {
  $routeProvider
    .when('/new/:type', {
      controller: 'ContentDeveloperMainController',
      template: MainViewTemplate,
      resolve: ContentDeveloperMainController.resolves ('create')
    })

    .when('/content/:contentId*', {
      controller: 'ContentDeveloperMainController',
      template: MainViewTemplate,
      resolve: ContentDeveloperMainController.resolves ('view')
    })

    .when('/edit/:contentId*', {
      controller: 'ContentDeveloperMainController',
      template: MainViewTemplate,
      resolve: ContentDeveloperMainController.resolves ('edit')
    })

    .when('/editVersion/:versionId*', {
      controller: 'ContentDeveloperEditVersionController',
      template: '<div></div>'
    })

    .when('/version/:versionId*', {
      controller: 'ContentDeveloperMainController',
      template: MainViewTemplate,
      resolve: ContentDeveloperMainController.resolves ('version')
    })

    .when('/search', {
      controller: 'ContentDeveloperFullSearchController',
      template: SearchTemplate,
      resolve: ContentDeveloperFullSearchController.resolves
    })

    .when('/error', {
      controller: 'ContentDeveloperErrorController',
      template: ErrorTemplate
    })

    .otherwise ({
      controller: 'ContentDeveloperDashboardCtrl',
      template: DashboardTemplate
    });

  $locationProvider.hashPrefix('');

  $translateProvider.useLoader('CodeTranslateLoader');
  $translateProvider.determinePreferredLanguage();
  $translateProvider.fallbackLanguage('en');
  $translateProvider.useSanitizeValueStrategy('escape');

  $provide.decorator('Application', ['$delegate', function ($delegate) {
    $delegate.context = {
      isTouchDevice: Modernizr.touchevents
    };

    return $delegate;
  }]);

  $provide.decorator('ContentEditor', ['$delegate', 'CodeContentEditor', function ($delegate, CodeContentEditor) {
    $delegate.openContent = (id, forceReload, mode) => {
      return CodeContentEditor.openContent(id, forceReload, mode);
    };

    $delegate.getContentLink = (contentId) => {
      return CodeContentEditor.getContentLink (contentId);
    };

    $delegate.editContent = (contentId) => {
      return CodeContentEditor.editContent(contentId);
    };

    $delegate.editVersion = (versionId, currentRevision) => {
      return CodeContentEditor.editVersion(versionId, currentRevision);
    };

    $delegate.openVersion = (versionId) => {
      return CodeContentEditor.openVersion(versionId);
    };

    return $delegate;
  }]);

  $provide.decorator('$q', ['$delegate', function ($delegate) {
    $delegate.allSettled = atex.aceui.AngularUtils.allSettled.bind($delegate, $delegate);
    return $delegate;
  }]);

  $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|ftp|mailto|skype):/);

  if (process.env.NODE_ENV === 'production') {
    $compileProvider.debugInfoEnabled(false);
    $compileProvider.commentDirectivesEnabled(false);
  }
});

app.run(function ($rootScope, $location, $translate, Application, AceUIAuth, KeyboardShortcutService, KeyboardShortcutsModal, ApplicationConfigurationService) {
  ApplicationConfigurationService.getConfigurationsFor(['Environment'])
    .then(([environmentConfiguration]) => {
      if (environmentConfiguration) {
        $rootScope.environment = {
          'color': environmentConfiguration.color,
          'name': environmentConfiguration.name
        };
      }
    });

  KeyboardShortcutService.universalSet({
    global: {
      'f1': function () {
        KeyboardShortcutsModal.open();
      }
    },

    '?': {
      action: function () {
        KeyboardShortcutsModal.open();
      },
      label: 'keyboard-shortcuts.show-help.label'
    }
  });

  moment.locale($translate.preferredLanguage());
  window.moment = moment;

  $('body').addClass(Application.context.isTouchDevice === true ? 'touch' : 'no-touch');

  $rootScope.$on('$routeChangeError', function (event, current, previous, rejection) {
    Application.currentErrorInfo = {error: rejection};
    $location.path('/error');
    $location.replace();
  });

  let authenticationInProgress = false;

  $rootScope.$on('aceui:authenticationRequest', (event, currentUser) => {
    // Do not open if already opened:
    if (authenticationInProgress) {
      return;
    }
    authenticationInProgress = true;
    AceUIAuth.signIn (currentUser)
      .then (() => {
        authenticationInProgress = false;
      })
      .catch ((error) => {
        console.error ('Really unexpected error when authenticated', error);
      });
  });
});
